/**
 * Transport Profile Service
 * Handles API calls to check if the current user has a transport unit assigned.
 * Follows the same pattern as gaushala/profileService.ts
 *
 * Uses the TenantInterceptor behavior:
 * - Any /api/** endpoint (except excluded paths) goes through the interceptor
 * - The interceptor checks transport_unit_access for the current user
 * - Returns 403 with "No transport unit access" if no unit is assigned
 *
 * IMPORTANT: The probe endpoint MUST be one that the TenantInterceptor intercepts.
 * Paths like /api/v1/lookup/** are excluded from tenant resolution (see WebMvcConfig)
 * and will return 200 for ANY authenticated user — making them useless as access probes.
 */

import { getServiceUrl } from '@/config/serviceConfig';

const API_BASE_URL = getServiceUrl('transport');

// ============================================================================
// CUSTOM ERROR CLASS
// ============================================================================

/**
 * Custom error class for transport-specific errors
 */
export class TransportError extends Error {
  constructor(
    message: string,
    public code: 'NO_UNIT_ASSIGNED' | 'API_ERROR' | 'UNKNOWN',
    public originalError?: Error
  ) {
    super(message);
    this.name = 'TransportError';
  }
}

// ============================================================================
// ERROR DETECTION
// ============================================================================

/** Error messages from TenantInterceptor indicating no unit assignment */
const NO_UNIT_MESSAGES = [
  'no transport unit access',
  'transport unit access is inactive',
  'user context not available',
  'no transport units configured',
  'manufacturing users cannot access this transport resource',
  'forbidden',
  'access denied',
];

// ============================================================================
// API METHODS
// ============================================================================

/**
 * Check if the current user has a transport unit assigned.
 * Calls a lightweight transport API endpoint that goes through TenantInterceptor.
 * The interceptor checks the transport_unit_access table for the current user's JWT.
 *
 * @returns Promise that resolves if user has a unit assigned
 * @throws TransportError with code NO_UNIT_ASSIGNED if no unit is assigned
 */
export const getTransportProfile = async (): Promise<any> => {
  try {
    // Probe a tenant-intercepted endpoint. /requests goes through TenantInterceptor —
    // returns 403 "No transport unit access" if not assigned, 200 (empty list) if assigned.
    // Previously used /analytics/dashboard/fleet-metrics which does not exist → 500 → false denial.
    const response = await fetch(`${API_BASE_URL}/api/v1/requests`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Transport Profile API Error Response:', {
        status: response.status,
        body: errorData,
      });

      // Parse the error message from the response
      const errorMessage = (
        errorData?.error?.message ||
        errorData?.message ||
        errorData?.error ||
        ''
      ).toLowerCase();

      // 403 from this probe means the TenantInterceptor denied access — either
      // no transport_unit_access record, inactive access, or role-based denial
      // (e.g. MFG_OPERATOR hitting a non-manufacturing-allowed path).
      // 404 means the resource doesn't exist for this tenant.
      const isNoUnitError =
        response.status === 403 ||
        response.status === 404 ||
        NO_UNIT_MESSAGES.some(msg => errorMessage.includes(msg));

      if (isNoUnitError) {
        throw new TransportError(
          'No transport unit is assigned to your account. Please contact your administrator.',
          'NO_UNIT_ASSIGNED'
        );
      }

      throw new TransportError(
        errorData?.message || `API Error: ${response.status} ${response.statusText}`,
        'API_ERROR'
      );
    }

    const result = await response.json();
    console.log('Transport Profile API Response: OK');

    return result.data || result;
  } catch (error) {
    console.error('Error fetching transport profile:', error);

    // Re-throw TransportError as is
    if (error instanceof TransportError) {
      throw error;
    }

    // Wrap other errors
    throw new TransportError(
      error instanceof Error ? error.message : 'Failed to load transport profile',
      'UNKNOWN',
      error instanceof Error ? error : undefined
    );
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getAuthToken = (): string => {
  return localStorage.getItem('saubhagya_jwt_token') || '';
};
